/** Platform console → one customer's activity: pick a requirement, then each candidate is a
 *  collapsible log group (open it to see what happened to them, dated — emails, link opened, form,
 *  resume, interview, decisions). Read-only; same log recruiters see (components/CandidateTimeline). */
import { useState } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { ActivityGroup } from "../../components/CandidateTimeline.js";

interface ReqRow {
  id: string;
  title: string;
  state: string;
  createdAt: string;
  candidates: number;
  lastAddedAt: string | null;
}

interface CandRow {
  candidacyId: string;
  name: string | null;
  email: string | null;
  state: string;
  pipelineStatus: string | null;
  source: string | null;
  createdAt: string;
  cvRequestStatus: string | null;
}

const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });

const SOURCE: Record<string, string> = { import: "Bulk import", email: "Applied by email" };
const CV: Record<string, string> = {
  sent: "CV requested",
  opened: "CV link opened",
  completed: "CV submitted",
  expired: "CV link expired",
  cancelled: "CV re-requested",
};

/** The one-line state shown on a candidate's group header, most telling fact first. */
function headline(c: CandRow): string {
  const tag = c.pipelineStatus ? ` · ${c.pipelineStatus.replace(/_/g, " ")}` : "";
  const cv = c.cvRequestStatus ? CV[c.cvRequestStatus] ?? `CV ${c.cvRequestStatus}` : null;
  return `${c.state.replace(/_/g, " ")}${cv ? ` · ${cv}` : ""}${tag}`;
}

export function OrgActivityLog({ orgId }: { orgId: string }): ReactNode {
  const reqs = useQuery({
    queryKey: ["admin-org-activity", orgId],
    queryFn: () => api.get<{ requisitions: ReqRow[] }>(`/admin/orgs/${orgId}/activity`),
  });
  const [reqId, setReqId] = useState<string>("");
  const [filter, setFilter] = useState("");
  const selected = reqId || reqs.data?.requisitions[0]?.id || "";
  const cands = useQuery({
    queryKey: ["admin-req-activity", selected],
    queryFn: () => api.get<{ candidates: CandRow[] }>(`/admin/requisitions/${selected}/activity`),
    enabled: Boolean(selected),
  });

  const list = (cands.data?.candidates ?? []).filter((c) => {
    const f = filter.trim().toLowerCase();
    return !f || `${c.name ?? ""} ${c.email ?? ""}`.toLowerCase().includes(f);
  });

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-h">
        <h3>Candidate activity log</h3>
      </div>
      <div className="card-b">
        {reqs.isLoading && <p className="mut sm">Loading requirements…</p>}
        {reqs.isError && <p className="mut sm">Couldn't load this customer's requirements.</p>}
        {reqs.data && reqs.data.requisitions.length === 0 && <p className="mut sm">No requirements yet.</p>}
        {reqs.data && reqs.data.requisitions.length > 0 && (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
              <select
                className="input"
                style={{ flex: "1 1 280px", minWidth: 0 }}
                aria-label="Requirement"
                value={selected}
                onChange={(e) => setReqId(e.target.value)}
              >
                {reqs.data.requisitions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.title} — {r.candidates} candidate{r.candidates === 1 ? "" : "s"} · {r.state.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
              <input
                className="input"
                style={{ flex: "1 1 200px", minWidth: 0 }}
                type="search"
                placeholder="Find a candidate by name or email"
                aria-label="Find a candidate"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>
            {cands.isLoading && <p className="mut sm">Loading candidates…</p>}
            {cands.isError && <p className="mut sm">Couldn't load candidates for this requirement.</p>}
            {cands.data && list.length === 0 && (
              <p className="mut sm">{filter ? "No candidate matches that search." : "No candidates on this requirement yet."}</p>
            )}
            {list.map((c) => (
              <ActivityGroup
                key={c.candidacyId}
                title={c.name || c.email || "Unnamed candidate"}
                subtitle={[c.email, c.source ? SOURCE[c.source] ?? c.source : null, `added ${day(c.createdAt)}`]
                  .filter(Boolean)
                  .join(" · ")}
                status={headline(c)}
                path={`/admin/candidacies/${c.candidacyId}/timeline`}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}
